import {
  CognitoIdentityClient,
  GetCredentialsForIdentityCommand,
  GetIdCommand,
} from '@aws-sdk/client-cognito-identity';
import type { IS3MediaClient, IUploadFile } from './types';
import {
  GetObjectCommand,
  PutObjectCommand,
  type PutObjectCommandOutput,
  S3Client,
} from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import ENV from 'scaleflow/config/environment';

export let s3MediaClient: S3MediaClient | null = null;

class S3MediaClient {
  // Use config from environment.js
  private readonly REGION = ENV.aws.region;
  private readonly USER_POOL_ID = ENV.cognito.userPoolId;
  private readonly IDENTITY_POOL_ID = ENV.cognito.identityPoolId;
  private readonly BUCKET = ENV.storage.AWSS3.bucket;

  private readonly loginKey: string;

  private s3Client: S3Client | null = null;
  private idToken: string;
  private tenantId: string;
  private credentialExpiration: Date | null = null;

  constructor({ idToken, tenantId }: IS3MediaClient) {
    if (!idToken) {
      throw new Error('idToken needed to instantiate S3MediaClient');
    }
    this.idToken = idToken;

    if (!tenantId) {
      throw new Error('tenantId needed to instantiate S3MediaClient');
    }
    this.tenantId = tenantId;
    this.loginKey = `cognito-idp.${this.REGION}.amazonaws.com/${this.USER_POOL_ID}`;
  }

  /**
   * Generates temporary AWS credentials using Cognito Identity Pool.
   */
  private async generateCredentials(): Promise<{
    credentials: {
      accessKeyId: string;
      secretAccessKey: string;
      sessionToken: string;
      expiration: Date;
    };
  }> {
    const cognitoClient = new CognitoIdentityClient({ region: this.REGION });

    const getIdCommand = new GetIdCommand({
      IdentityPoolId: this.IDENTITY_POOL_ID,
      Logins: {
        [this.loginKey]: this.idToken,
      },
    });
    const getIdResponse = await cognitoClient.send(getIdCommand);
    const identityId = getIdResponse.IdentityId;
    if (!identityId) {
      throw new Error('Failed to get IdentityId from Cognito Identity Pool.');
    }

    const getCredentialsCommand = new GetCredentialsForIdentityCommand({
      IdentityId: identityId,
      Logins: {
        [this.loginKey]: this.idToken,
      },
    });
    const getCredentialsResponse = await cognitoClient.send(
      getCredentialsCommand,
    );
    const credentials = getCredentialsResponse.Credentials;
    if (!credentials) {
      throw new Error('Failed to get credentials from Cognito Identity Pool.');
    }

    return {
      credentials: {
        accessKeyId: credentials.AccessKeyId ?? '',
        secretAccessKey: credentials.SecretKey ?? '',
        sessionToken: credentials.SessionToken ?? '',
        expiration: credentials.Expiration
          ? new Date(credentials.Expiration)
          : new Date(),
      },
    };
  }

  private async refreshCredentials(): Promise<void> {
    const { credentials } = await this.generateCredentials();
    this.credentialExpiration = credentials.expiration;
    this.s3Client = new S3Client({
      region: this.REGION,
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken,
      },
      requestChecksumCalculation: 'WHEN_REQUIRED',
    });
  }

  private isCredentialExpiringSoon(): boolean {
    if (!this.credentialExpiration) return true;
    const FIVE_MINUTES_MS = 5 * 60 * 1000;
    return this.credentialExpiration.getTime() - Date.now() <= FIVE_MINUTES_MS;
  }

  private async ensureCredentialsValid(): Promise<void> {
    if (this.isCredentialExpiringSoon()) {
      console.debug('S3MediaClient credentials expiring soon, refreshing...');
      await this.refreshCredentials();
    }
  }

  /**
   * Initializes the S3 client with temporary credentials.
   */
  public async initializeClient(): Promise<void> {
    if (this.s3Client) {
      console.debug('S3MediaClient already initialized.');
      return;
    }
    await this.refreshCredentials();
    console.debug('Media client initialized');
  }

  public updateToken(idToken: string): void {
    this.idToken = idToken;
  }

  /**
   * Uploads a file to the S3 bucket.
   * @param key The S3 key (file path)
   * @param body The file content
   * @param contentType The MIME type of the file
   * @returns Promise resolving to the upload result and the full S3 key
   */
  public async uploadFile({
    key,
    body,
    contentType,
  }: IUploadFile): Promise<{ result: PutObjectCommandOutput; key: string }> {
    await this.ensureCredentialsValid();

    // Prepend 'public/' to match Amplify's default behavior and IAM permissions
    const fullKey = `public/${this.tenantId}/${key}`;

    const command = new PutObjectCommand({
      Bucket: this.BUCKET,
      Key: fullKey,
      Body: body,
      ContentType: contentType,
    });

    const result = await this.s3Client!.send(command);
    return { result, key: fullKey };
  }

  /**
   * Gets a signed URL for a file in the S3 bucket.
   * @param key The S3 key (file path)
   * @param expiresIn URL expiration time in seconds (default: 3600 = 1 hour)
   * @returns Promise resolving to the signed URL string
   */
  public async getSignedUrl(
    key: string,
    expiresIn: number = 3600,
  ): Promise<string> {
    await this.ensureCredentialsValid();

    const command = new GetObjectCommand({
      Bucket: this.BUCKET,
      Key: key,
    });

    return await getSignedUrl(this.s3Client!, command, { expiresIn });
  }

  /**
   * Uploads a file and returns a signed URL for accessing it.
   * @param key The S3 key (file path) - will be prefixed with 'public/'
   * @param body The file content
   * @param contentType The MIME type of the file
   * @param urlExpiresIn URL expiration time in seconds (default: 3600 = 1 hour)
   * @returns Promise resolving to the signed URL string
   */
  public async uploadAndGetUrl(
    key: string,
    body: File | Blob,
    contentType: string,
    urlExpiresIn: number = 3600,
  ): Promise<string> {
    const { key: fullKey } = await this.uploadFile({ key, body, contentType });
    return await this.getSignedUrl(fullKey, urlExpiresIn);
  }
}

/**
 * Initializes the singleton S3MediaClient instance.
 * @param idToken The Cognito ID token.
 */
export async function initializeMediaClient(
  idToken: string,
  tenantId: string,
): Promise<S3MediaClient> {
  if (s3MediaClient) {
    s3MediaClient.updateToken(idToken);
    return s3MediaClient;
  }
  s3MediaClient = new S3MediaClient({ idToken, tenantId });
  await s3MediaClient.initializeClient();
  return s3MediaClient;
}

export default initializeMediaClient;
